import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { withApollo } from "react-apollo";
import { OwcTypography, OwcButton, OwcModalDialog } from "@one/react";
import { BOOKING_GET_CHECKLISTS_FOR_EQUIPMENTS } from "../../gql/bookingapi";
import { ViewChecklist } from "../../components/shared/CommonPopOverModal/ViewChecklist";
import ChecklistFolder from "../../icons/ChecklistFolder";
import { getFieldValue } from "../booking-instruments/helpers";

const checklistCache = new Map();

// Helper: resolve a field from data, with fallbacks through equipment sub-object and camelCase variant
const resolveField = (data, snakeKey, camelKey) =>
  getFieldValue(data, snakeKey) ||
  getFieldValue(data?.equipment, camelKey) ||
  getFieldValue(data, camelKey);

// Helper: fetch checklists from the API for a given cache key
const fetchChecklistData = async (client, key) => {
  const res = await client.query({
    query: BOOKING_GET_CHECKLISTS_FOR_EQUIPMENTS,
    variables: { keys: [key] },
    fetchPolicy: "no-cache",
  });
  const entries = res?.data?.getChecklistsForEquipments || [];
  const found = entries.find((e) => e.key === key);
  return found?.checklists || [];
};

const ChecklistRender = ({ client, ...params }) => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { data } = params || {};
  const model = resolveField(data, "equipment_model", "equipmentModel");
  const site = resolveField(data, "site_name", "siteName");
  const group = resolveField(data, "belonging_to_group", "belongingToGroup");
  const key = `${site}#${group}#${model}`;

  useEffect(() => {
    if (!client || !key) return;

    const cached = checklistCache.get(key);
    if (cached) {
      setChecklists(cached);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const list = await fetchChecklistData(client, key);
        checklistCache.set(key, list);
        setChecklists(list);
      } catch (err) {
        console.warn("Checklist fetch failed:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [client, key]);

  if (loading) return "-";
  if (!checklists?.length) return "-";

  const handleOpenModal = (e) => {
    e.stopPropagation();
    if (checklists.length === 1) {
      const single = checklists[0];
      if (single?.url) {
        window.open(single.url, "_blank", "noopener,noreferrer");
        return;
      }
    }
    setModalOpen(true);
  };

  const handleCloseModal = (e) => {
    e.stopPropagation();
    setModalOpen(false);
  };

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <button
        onClick={handleOpenModal}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
        }}
        title={
          checklists.length === 1
            ? checklists[0]?.description || "-"
            : `Show checklists for ${model}`
        }
      >
        <ChecklistFolder />
      </button>

      {modalOpen &&
        ReactDOM.createPortal(
          <OwcModalDialog
            disableScrollLock
            visible={modalOpen}
            size="m"
            className="owcmodalZIndex"
            onVisibleChange={(e) => {
              e.stopPropagation();
              if (!e.target.visible) handleCloseModal(e);
            }}
          >
            <div slot="header">
              <OwcTypography variant="button">
                Checklists
              </OwcTypography>
            </div>

            <ViewChecklist
              equipmentDetails={data?.equipment || data}
              checklists={checklists}
              loading={loading}
              minHeight={200}
            />

            <div slot="actions" style={{ marginLeft: "auto", display: "flex", justifyContent: "end" }}>
              <OwcButton variant="secondary" onClick={handleCloseModal}>
                Close
              </OwcButton>
            </div>
          </OwcModalDialog>,
          document.body
        )}
    </div>
  );
};

export default withApollo(ChecklistRender);
